import { Users, Target, Heart, Award } from "lucide-react";

const ABOUT_IMAGE = "https://images.pexels.com/photos/1653898/pexels-photo-1653898.jpeg";

const values = [
  {
    icon: Heart,
    title: "Dedizione",
    description: "Trattiamo ogni pratica con la massima cura e attenzione, come se fosse la nostra."
  },
  {
    icon: Target,
    title: "Professionalità",
    description: "Personale qualificato e costantemente aggiornato sulle normative vigenti."
  },
  {
    icon: Users,
    title: "Vicinanza",
    description: "Un punto di riferimento per la comunità di Margherita di Savoia e dintorni."
  },
  {
    icon: Award,
    title: "Affidabilità",
    description: "Anni di esperienza e migliaia di pratiche gestite con successo."
  }
];

const ChiSiamo = () => {
  return (
    <div className="min-h-screen">
      {/* Hero */}
      <section className="bg-slate-900 py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <span className="text-sm font-semibold tracking-[0.1em] uppercase text-slate-400 mb-4 block">
              Chi Siamo
            </span>
            <h1 className="font-heading text-4xl md:text-6xl font-black tracking-tighter text-white mb-6">
              La tua guida nella burocrazia italiana
            </h1>
            <p className="text-xl text-slate-300 leading-relaxed">
              Da oltre 15 anni al servizio dei cittadini di Margherita di Savoia e della provincia di Barletta-Andria-Trani.
            </p>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="font-heading text-3xl md:text-4xl font-bold tracking-tight text-slate-900 mb-6">
                Il nostro impegno per la comunità
              </h2>
              <div className="prose prose-lg text-slate-600">
                <p className="mb-6">
                  Il <strong>Centro Servizi CAF & Patronato EPAS</strong> di Margherita di Savoia nasce dalla volontà di offrire un servizio di assistenza fiscale e previdenziale di qualità, accessibile a tutti i cittadini.
                </p>
                <p className="mb-6">
                  Siamo affiliati a <strong>CAF Italia Srl</strong>, <strong>Patronato EPAS</strong>, <strong>FNA Confsal</strong> e <strong>U.Di.Con</strong> (Unione per la Difesa dei Consumatori), garantendo così un network di competenze e supporto a 360 gradi.
                </p>
                <p className="mb-6">
                  Il nostro team di professionisti è costantemente aggiornato sulle normative fiscali e previdenziali per offrire sempre il miglior servizio possibile. Crediamo che la burocrazia non debba essere un ostacolo, ma un percorso che possiamo affrontare insieme.
                </p>
                <p>
                  Che tu abbia bisogno di compilare un ISEE, presentare il modello 730, richiedere una pensione o gestire pratiche di immigrazione, siamo qui per guidarti passo dopo passo.
                </p>
              </div>
            </div>
            <div className="relative">
              <img
                src={ABOUT_IMAGE}
                alt="Persone anziane felici"
                className="rounded-2xl shadow-2xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="py-24 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="text-sm font-semibold tracking-[0.1em] uppercase text-slate-500 mb-4 block">
              I Nostri Valori
            </span>
            <h2 className="font-heading text-3xl md:text-4xl font-bold tracking-tight text-slate-900">
              Cosa ci guida ogni giorno
            </h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, idx) => (
              <div 
                key={idx} 
                className="bg-white p-8 rounded-2xl border border-slate-200 card-hover"
                data-testid={`value-card-${idx}`}
              >
                <div className="w-14 h-14 bg-blue-900 rounded-xl flex items-center justify-center mb-6">
                  <value.icon className="w-7 h-7 text-white" />
                </div>
                <h3 className="font-heading text-xl font-bold text-slate-900 mb-3">
                  {value.title}
                </h3>
                <p className="text-slate-600">
                  {value.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Affiliations */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="text-sm font-semibold tracking-[0.1em] uppercase text-slate-500 mb-4 block">
              Le Nostre Affiliazioni
            </span>
            <h2 className="font-heading text-3xl md:text-4xl font-bold tracking-tight text-slate-900 mb-4">
              Un network di eccellenza
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Siamo orgogliosamente affiliati alle principali organizzazioni del settore, garantendo servizi di altissima qualità.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="bg-slate-50 p-8 rounded-2xl text-center">
              <h3 className="font-heading text-2xl font-bold text-blue-900 mb-2">CAF Italia Srl</h3>
              <p className="text-slate-600">Centro di Assistenza Fiscale</p>
            </div>
            <div className="bg-slate-50 p-8 rounded-2xl text-center">
              <h3 className="font-heading text-2xl font-bold text-blue-900 mb-2">EPAS</h3>
              <p className="text-slate-600">Patronato per l'assistenza sociale</p>
            </div>
            <div className="bg-slate-50 p-8 rounded-2xl text-center">
              <h3 className="font-heading text-2xl font-bold text-blue-900 mb-2">FNA Confsal</h3>
              <p className="text-slate-600">Federazione Nazionale Agricoltura</p>
            </div>
            <div className="bg-slate-50 p-8 rounded-2xl text-center">
              <h3 className="font-heading text-2xl font-bold text-blue-900 mb-2">U.Di.Con</h3>
              <p className="text-slate-600">Unione per la Difesa dei Consumatori</p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ChiSiamo;
